import { useEffect, useState } from 'react'
import { getServiceLabel } from '../helpers/services.helper'
import { t } from '../locale'
import { fetchServiceUsageDistribution } from '../service/subscription'

const CATEGORICAL_COLORS = ['#2a78d6', '#eb6834', '#1baf7a', '#eda100', '#e87ba4', '#008300']
const OTHER_COLOR = '#898781'
const MAX_SLICES = 6

const SIZE = 120
const CENTER = SIZE / 2
const RADIUS = 45
const STROKE_WIDTH = 14
const GAP = 1.5
const CIRCUMFERENCE = 2 * Math.PI * RADIUS

interface Slice {
  key: string
  label: string
  count: number
  // Exact 0-1 share of the total, used to draw the arc so rounding errors in the
  // displayed percent (below) can't leave a gap where slices don't quite meet.
  ratio: number
  percent: number
  color: string
}

function buildMonthOptions(count: number): { value: string; label: string }[] {
  const now = new Date()

  return Array.from({ length: count }, (_, i) => {
    const date = new Date(now.getFullYear(), now.getMonth() - i, 1)
    const value = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`
    const label = date.toLocaleDateString(undefined, { month: 'long', year: 'numeric' })

    return { value, label }
  })
}

function buildSlices(distribution: { service: string; count: number }[]): Slice[] {
  const total = distribution.reduce((sum, d) => sum + d.count, 0)
  if (total === 0) return []

  const top = distribution.slice(0, MAX_SLICES)
  const rest = distribution.slice(MAX_SLICES)
  const restCount = rest.reduce((sum, d) => sum + d.count, 0)

  const slices: Slice[] = top.map((d, i) => ({
    key: d.service,
    label: getServiceLabel(d.service),
    count: d.count,
    ratio: d.count / total,
    percent: Math.round((d.count / total) * 100),
    color: CATEGORICAL_COLORS[i],
  }))

  if (restCount > 0) {
    slices.push({
      key: 'other',
      label: t('otherServices'),
      count: restCount,
      ratio: restCount / total,
      percent: Math.round((restCount / total) * 100),
      color: OTHER_COLOR,
    })
  }

  return slices
}

const MONTH_OPTIONS = buildMonthOptions(12)

function ServiceUsageDonutChart() {
  const [slices, setSlices] = useState<Slice[] | null>(null)
  const [total, setTotal] = useState(0)
  const [month, setMonth] = useState('')

  useEffect(() => {
    setSlices(null)
    fetchServiceUsageDistribution(month || undefined).then(distribution => {
      if (!distribution) {
        setSlices([])
        return
      }
      setTotal(distribution.reduce((sum, d) => sum + d.count, 0))
      setSlices(buildSlices(distribution))
    })
  }, [month])

  let cumulativeRatio = 0

  return (
    <div className='border rounded p-3 bg-white shadow-sm mb-4 h-100 d-flex flex-column'>
      <div className='d-flex align-items-center justify-content-between mb-2'>
        <div className='fw-semibold text-draft'>{t('serviceUsage')}</div>
        <select
          className='form-select form-select-sm w-auto'
          value={month}
          onChange={e => setMonth(e.target.value)}
          style={{
            borderColor: '#dee2e6',
            borderRadius: '0.375rem',
            boxShadow: '0 0.125rem 0.25rem rgba(0, 0, 0, 0.075)',
            cursor: 'pointer',
            transition: 'border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out',
          }}
          onFocus={e => {
            e.currentTarget.style.borderColor = '#2a78d6'
            e.currentTarget.style.boxShadow = '0 0 0 0.2rem rgba(42, 120, 214, 0.25)'
          }}
          onBlur={e => {
            e.currentTarget.style.borderColor = '#dee2e6'
            e.currentTarget.style.boxShadow = '0 0.125rem 0.25rem rgba(0, 0, 0, 0.075)'
          }}
        >
          <option value=''>{t('allTime')}</option>
          {MONTH_OPTIONS.map(opt => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>

      <div className='flex-grow-1 d-flex flex-column justify-content-center'>
        {slices === null && (
          <div className='text-muted small text-center py-3'>{t('Loading...')}</div>
        )}

        {slices?.length === 0 && (
          <div className='text-muted small text-center py-3'>{t('noData')}</div>
        )}

        {!!slices?.length && (
          <div className='row align-items-center g-4'>
            <div className='col-auto mx-auto'>
              <svg width={SIZE} height={SIZE} viewBox={`0 0 ${SIZE} ${SIZE}`}>
                <g transform={`rotate(-90 ${CENTER} ${CENTER})`}>
                  {slices.map(slice => {
                    const dash = Math.max(slice.ratio * CIRCUMFERENCE - GAP, 0)
                    const offset = -(cumulativeRatio * CIRCUMFERENCE)
                    cumulativeRatio += slice.ratio

                    return (
                      <circle
                        key={slice.key}
                        cx={CENTER}
                        cy={CENTER}
                        r={RADIUS}
                        fill='none'
                        stroke={slice.color}
                        strokeWidth={STROKE_WIDTH}
                        strokeDasharray={`${dash} ${CIRCUMFERENCE - dash}`}
                        strokeDashoffset={offset}
                      />
                    )
                  })}
                </g>
                <text
                  x={CENTER}
                  y={CENTER - 2}
                  textAnchor='middle'
                  className='fw-bold'
                  style={{ fontSize: 15, fill: '#0b0b0b' }}
                >
                  {total}
                </text>
                <text
                  x={CENTER}
                  y={CENTER + 8}
                  textAnchor='middle'
                  style={{ fontSize: 8, fill: '#898781' }}
                >
                  {t('total')}
                </text>
              </svg>
            </div>

            <div className='col'>
              <ul className='list-unstyled mb-0' style={{ fontSize: '0.8rem' }}>
                {slices.map(slice => (
                  <li key={slice.key} className='d-flex align-items-center mb-1'>
                    <span
                      className='rounded-circle me-1 flex-shrink-0'
                      style={{ width: 6, height: 6, backgroundColor: slice.color }}
                    />
                    <span className='text-draft text-truncate'>{slice.label}</span>
                    <span className='text-muted ms-auto ps-2'>{slice.percent}%</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default ServiceUsageDonutChart
